/**
 * Macro #26 · Increment 2 — LAZY loader for the XYZ editor canvas (NO three).
 * Same lazy/boundary contract as LazyVoxelScene.
 */
import { lazy, Suspense, useMemo, type ReactNode } from "react";
import { WebGLBoundary } from "./WebGLBoundary";
import { isWebGLAvailable } from "./webgl";
import type { Voxel } from "../domain/voxel3d";

interface LocalVoxel { x: number; y: number; z: number; color: string }

const VoxelEditorCanvas = lazy(() => import("./VoxelEditorCanvas"));

export function LazyVoxelEditorCanvas(props: {
  activeVoxels: Voxel[];
  otherVoxels: LocalVoxel[];
  footprintW: number;
  footprintH: number;
  mode: "add" | "remove" | "define";
  onAddVoxel: (lx: number, ly: number, lz: number) => void;
  onRemoveVoxel: (index: number) => void;
  resetSignal?: number;
  highlightGround?: boolean;
  onDefineBase?: (x: number, y: number, w: number, h: number) => void;
  onDefineDraftChange?: (draft: { w: number; h: number; valid: boolean } | null) => void;
  existingBases?: Array<{ x: number; y: number; w: number; h: number }>;
  defineMaxCells?: number;
  fallback: ReactNode;
  loading: ReactNode;
  onError?: () => void;
}) {
  const ok = useMemo(() => isWebGLAvailable(), []);
  if (!ok) return <>{props.fallback}</>;
  const { fallback, loading, onError, ...scene } = props;
  return (
    <WebGLBoundary fallback={fallback} onError={onError}>
      <Suspense fallback={loading}>
        <VoxelEditorCanvas {...scene} />
      </Suspense>
    </WebGLBoundary>
  );
}
